"use client";

import * as React from "react";
import { Clock, ArrowRight, CircleAlert, Loader } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
const getReadTime = (content?: string) => {
  if (!content) return "5 min read";
  const wordsPerMinute = 200;
  const words = content.trim().split(/\s+/).length;
  const time = Math.ceil(words / wordsPerMinute);
  return `${time} min read`;
};
function BlogGridConsumer() {
  const {
    posts,
    loading,
    error,
    loadMore,
    hasNext,
    isRefetching
  } = usePosts();
  if (loading && posts.length === 0) {
    return <div data-section-id="52407" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {[...Array(6)].map((_, i) => <div key={i} className="flex flex-col gap-4" data-index={i}>
            <div className="w-full aspect-video bg-muted/20 animate-pulse rounded-sm" />
            <div className="h-6 w-24 bg-muted/20 animate-pulse rounded-full" />
            <div className="h-8 w-3/4 bg-muted/20 animate-pulse rounded-md" />
            <div className="h-4 w-full bg-muted/20 animate-pulse rounded-md" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center py-12 text-center text-muted-foreground">
        <CircleAlert className="h-10 w-10 mb-4 text-destructive" />
        <p className="font-sans">Unable to load our latest electrical insights at this time.</p>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="text-center py-12 text-muted-foreground">
        <p className="font-sans">No electrical articles found.</p>
      </div>;
  }
  return <div className="space-y-12">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
        {posts.map((post, i) => {
        const imageUrl = post.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1603327/img/generic_office_meeting.png";
        const category = post.categories?.[0]?.name || "Circuit Safety";
        const readTime = getReadTime(post.content);
        return <Card key={post.id} data-index={i} className="group border border-border/50 shadow-sm hover:shadow-lg bg-card rounded-sm flex flex-col h-full gap-0 overflow-visible transition-all duration-400">
              <Link to={post.link || "#"} className="block w-full mb-6">
                <div className="overflow-hidden rounded-t-sm border-b border-border/40 transition-all duration-500">
                  <AspectRatio ratio={16 / 9}>
                    <img src={imageUrl} alt={post.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </AspectRatio>
                </div>
              </Link>

              <CardHeader className="p-6 pt-0 space-y-3 mb-3">
                <div className="flex items-center justify-between">
                  <Badge variant="secondary" className="rounded-sm bg-primary/10 text-primary hover:bg-primary/20 px-3 py-1 text-[10px] uppercase tracking-widest font-mono">
                    {category}
                  </Badge>
                  <div className="flex items-center text-[10px] text-muted-foreground font-mono uppercase tracking-tight">
                    <Clock className="mr-1.5 h-3.5 w-3.5" />
                    {readTime}
                  </div>
                </div>
                <Link to={post.link || "#"} className="block">
                  <h3 className="font-sans font-bold text-2xl leading-tight text-foreground group-hover:text-primary transition-colors tracking-tight">
                    {post.title}
                  </h3>
                </Link>
              </CardHeader>

              <CardContent className="p-6 pt-0 mb-6 flex-grow">
                <p className="text-muted-foreground line-clamp-3 text-sm leading-relaxed font-sans">
                  {post.excerpt ? <span dangerouslySetInnerHTML={{
                __html: post.excerpt
              }} /> : "Discover professional tips on maintaining your home's electrical system, from panel upgrades to generator safety and energy efficiency."}
                </p>
              </CardContent>

              <CardFooter className="p-6 pt-0 mt-auto">
                <Link to={post.link || "#"} className="group/link inline-flex items-center text-sm font-bold text-primary hover:text-destructive transition-colors uppercase tracking-wider">
                  Read Article
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover/link:translate-x-1" />
                </Link>
              </CardFooter>
            </Card>;
      })}
      </div>

      {hasNext && <div className="flex justify-center pt-8 border-t border-border/40">
          <Button variant="outline" size="lg" onClick={async () => await loadMore()} disabled={isRefetching} className="min-w-[200px] rounded-sm border-primary/20 hover:bg-primary/5 hover:text-primary transition-all duration-300 font-bold uppercase tracking-widest text-xs">
            {isRefetching ? <>
                <Loader className="mr-2 h-4 w-4 animate-spin" />
                Updating...
              </> : "Load More Insights"}
          </Button>
        </div>}
    </div>;
}
export default function LatestWisdom() {
  const wp_query_params = {
    per_page: 6,
    orderby: "date",
    order: "desc",
    status: "publish"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="electrical-insights" className="py-24 bg-background text-foreground relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent opacity-50" />
      
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
          <div className="max-w-2xl">
            <h2 className="text-xs font-mono tracking-[0.2em] text-primary uppercase mb-3">
              Powering Your Knowledge
            </h2>
            <h1 className="font-sans text-4xl md:text-5xl text-foreground font-extrabold tracking-tighter">
              Electrical Insights
            </h1>
            <p className="mt-4 text-lg text-muted-foreground max-w-xl font-sans">
              Expert advice on residential wiring, commercial safety standards, and the latest in generator technology from Humberg's master electricians.
            </p>
          </div>
          <div className="hidden md:block pb-2">
            <Link to="/BlogPage">
              <Button variant="ghost" className="group text-muted-foreground hover:text-primary font-bold uppercase tracking-widest text-xs">
                View All Articles
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </div>
        </div>

        <PostsProvider wp_query={{
        per_page: 6,
        orderby: "date",
        order: "desc",
        status: ["publish"]
      }} className="min-h-[400px]">
          <BlogGridConsumer />
        </PostsProvider>
        
        <div className="md:hidden mt-12 text-center">
          <Link to="/BlogPage">
            <Button variant="outline" className="w-full rounded-sm font-bold uppercase tracking-widest text-xs">
              View All Articles
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}